import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import ScreenshotModal from '../components/ui/ScreenshotModal';
import EmergencyControl from '../components/ui/EmergencyControl';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { useDocumentTitle, dateFormat } from '../config/utils';
import { connectivityBadge } from '../utils/connectivityBadge';

// 2026-10-07 presence-states follow-up: Standby/App Closed ADDED alongside
// the existing four — Online/Offline/Warning keep their exact prior
// meaning (see matchFilter below), nothing about them changed.
const FILTERS = ['All', 'Online', 'Standby', 'App Closed', 'Offline', 'Warning'];

export default function Support() {
  useDocumentTitle('Support Dashboard');
  const { showToast } = useGlobal();
  const [devices, setDevices]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('All');
  const [search, setSearch]         = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [msgModal, setMsgModal]     = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(false);
  const [message, setMessage]       = useState('');
  const intervalRef = useRef(null);

  const fetchDevices = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // /admin/support/devices joins the active campaign for today and aliases
      // last_login → last_heartbeat. The older /datatable/device/view path
      // returned raw Device rows with no campaign join, which is why the
      // Campaign + Last Seen cells were always empty.
      const res = await adminApi.get('/support/devices');
      const list = res.data?.data?.devices ?? res.data?.data ?? [];
      setDevices(Array.isArray(list) ? list : []);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Auto-refresh every 15s, matching the other online-status surfaces —
  // this page's filter counts + per-card badges are REST-only (is_online),
  // no RTDB check, so keeping it in step with the 15s cadence elsewhere is
  // the whole mitigation.
  useEffect(() => {
    fetchDevices();
    intervalRef.current = setInterval(() => fetchDevices(true), 15000);
    return () => clearInterval(intervalRef.current);
  }, [fetchDevices]);

  const filtered = devices.filter(d => {
    const matchFilter =
      filter === 'All' ||
      // is_online is cast to boolean on the backend Device model — match
      // truthily so true / 1 / "1" all count as online. UNCHANGED.
      (filter === 'Online'  && d.is_online) ||
      (filter === 'Offline' && !d.is_online && d.status !== 'WARNING') ||
      (filter === 'Warning' && d.status === 'WARNING') ||
      // NEW — connectivity.state, additive, does not touch the three above.
      (filter === 'Standby' && d.connectivity?.state === 'standby') ||
      (filter === 'App Closed' && d.connectivity?.state === 'app_closed');
    const matchSearch = !search ||
      d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.serial_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.company_name?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const sendCommand = async (deviceId, command) => {
    // Screenshot is a request-then-view flow, not fire-and-forget — hand off
    // to the ScreenshotModal which both triggers and polls Firebase Storage.
    if (command === 'screenshot') {
      const dev = devices.find(d => d.id === deviceId);
      setSelectedDevice(dev ?? { id: deviceId });
      setScreenshotModal(true);
      return;
    }
    try {
      await adminApi.post(`/device/${deviceId}/command`, { command });
      showToast('success', 'Command Sent', `${command} sent to device.`);
    } catch {
      showToast('error', 'Failed', 'Could not send command.');
    }
  };

  const resetClaim = async (deviceId) => {
    if (!window.confirm(
      'Reset this device for re-pairing?\n\n' +
      'This unclaims the device: company, owner, campaigns, and tokens are ' +
      'all wiped. The device will show a new 6-digit pairing code on its ' +
      'next boot, ready to be claimed by a new customer.\n\n' +
      'This is destructive. Use Reissue Token if the same owner just lost ' +
      'their local token.'
    )) return;
    try {
      const { data } = await adminApi.post(`/device/${deviceId}/reset-claim`);
      showToast('success', 'Device Reset',
        data?.message ?? `New pairing code: ${data?.code ?? '—'}`);
      fetchDevices(true);
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not reset device.');
    }
  };

  const reissueToken = async (deviceId) => {
    if (!window.confirm(
      'Reissue authentication token for this device?\n\n' +
      'The current token will be revoked. The player will pick up the ' +
      'new token from Firebase RTDB within a few seconds; no reboot is required.'
    )) return;
    try {
      const { data } = await adminApi.post(`/device/${deviceId}/reissue-token`);
      showToast('success', 'Token Reissued', data?.message ?? 'New token delivered via RTDB.');
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not reissue token.');
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !selectedDevice) return;
    try {
      // Backend pushes a Firebase RTDB `message` action; the Android player
      // (NotificationActions.Message in MediaScreen.kt) pauses the campaign
      // and speaks the message via in-app TTS. Language/voice default to
      // Arabic on the backend — override here once we surface those in the UI.
      await adminApi.post(`/device/${selectedDevice.id}/message`, { message });
      showToast('success', 'Message Sent', 'Announcement queued on the device.');
      setMsgModal(false);
      setMessage('');
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not send message.');
    }
  };

  // 2026-10-07 — per-card badge now distinguishes all 4 connectivity
  // states (via the shared connectivityBadge helper), falling back to the
  // existing WARNING-status badge only once a device is truly offline —
  // same precedence the old is_online-first check already had.
  const cardBadge = (d) => {
    const state = d.connectivity?.state;
    if (state === 'online' || state === 'standby' || state === 'app_closed') {
      return connectivityBadge(state);
    }
    if (d.status === 'WARNING') return { variant: 'gold', label: 'Warning' };
    return { variant: 'danger', label: 'Offline' };
  };

  const online     = devices.filter(d => d.is_online).length;
  const offline    = devices.filter(d => !d.is_online).length;
  const standby    = devices.filter(d => d.connectivity?.state === 'standby').length;
  const appClosed  = devices.filter(d => d.connectivity?.state === 'app_closed').length;

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Support Dashboard</h1>
          <p className="text-text-muted text-sm mt-0.5">
            Real-time device monitoring · Auto-refresh every 30s ·
            <span className="text-teal ml-1">{online} online</span>
            <span className="ml-2">{standby} standby</span>
            <span className="ml-2">{appClosed} app closed</span>
            <span className="text-danger ml-2">{offline} offline</span>
          </p>
        </div>
        <button onClick={() => fetchDevices()} className="btn-secondary">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1 bg-bg-card border border-white/[0.08] rounded-lg p-1">
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search device, company…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input-base flex-1 max-w-xs py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(device => (
            <DeviceCard
              key={device.id}
              device={device}
              badge={cardBadge(device)}
              onCommand={sendCommand}
              onMessage={() => { setSelectedDevice(device); setMsgModal(true); }}
              onReissueToken={reissueToken}
              onResetClaim={resetClaim}
              onEmergencyChanged={() => fetchDevices(true)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-text-muted">No devices match your filter.</div>
          )}
        </div>
      )}

      {/* Screenshot viewer */}
      <ScreenshotModal
        open={screenshotModal}
        device={selectedDevice}
        onClose={() => setScreenshotModal(false)}
      />

      {/* Send Message Modal */}
      <Modal
        open={msgModal}
        onClose={() => setMsgModal(false)}
        title={`Send Message to ${selectedDevice?.name ?? selectedDevice?.serial_name}`}
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setMsgModal(false)}>Cancel</button>
            <button className="btn-primary" onClick={sendMessage}>Send</button>
          </>
        }
      >
        <textarea
          className="input-base h-28 resize-none"
          placeholder="Type your message to the device…"
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
      </Modal>
    </Layout>
  );
}

function relativeSince(ts) {
  if (!ts) return null;
  const t = typeof ts === 'string' ? Date.parse(ts) : new Date(ts).getTime();
  if (Number.isNaN(t)) return null;
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60)        return `${sec}s ago`;
  if (sec < 3600)      return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)     return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function DeviceCard({ device, badge, onCommand, onMessage, onReissueToken, onResetClaim, onEmergencyChanged }) {
  const campaignName = device.current_campaign_name ?? device.current_campaign;
  // Unified-presence rollout, part 2 (2026-10-06): prefer connectivity.last_seen_at
  // (same instant as last_heartbeat/last_login today, converges every reader
  // on one field).
  const heartbeat    = device.connectivity?.last_seen_at ?? device.last_heartbeat ?? device.last_login;
  const lastSeenAbs  = heartbeat ? dateFormat(heartbeat) : null;
  const lastSeenRel  = heartbeat ? relativeSince(heartbeat) : null;

  return (
    <div className="card-padded flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text-main truncate">{device.name ?? device.serial_name}</p>
          <p className="text-xs text-text-muted truncate">{device.company_name}</p>
        </div>
        <Badge variant={badge.variant} dot>{badge.label}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-white/[0.03] rounded-lg p-2.5">
          <p className="text-text-muted mb-0.5">Serial</p>
          <p className="text-text-main font-mono truncate">{device.serial_name}</p>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-2.5">
          <p className="text-text-muted mb-0.5">Campaign</p>
          <p className="text-text-main truncate" title={campaignName ?? 'No active show'}>
            {campaignName ?? <span className="text-text-muted/70">No active show</span>}
          </p>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-2.5">
          <p className="text-text-muted mb-0.5">Last Seen</p>
          <p className="text-text-main" title={lastSeenAbs ?? '—'}>
            {lastSeenRel ?? '—'}
          </p>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-2.5">
          <p className="text-text-muted mb-0.5">Rotation</p>
          <p className="text-text-main">{device.rotation ?? '0'}°</p>
        </div>
      </div>

      {/* Action buttons — always render an icon, hide the text on narrow
          widths so each button still has a visible label on desktop. The
          `title` attribute provides the bilingual tooltip on all screen sizes. */}
      <div className="flex gap-1.5 pt-1">
        <ActionButton
          onClick={() => onCommand(device.id, 'restart')}
          icon="↺"
          label="Restart"
          tooltip="Restart device · إعادة تشغيل"
          hoverClass="hover:bg-accent/10 hover:text-accent"
        />
        <ActionButton
          onClick={() => onCommand(device.id, 'clear_cache')}
          icon="⌫"
          label="Clear Cache"
          tooltip="Clear cache · مسح التخزين"
          hoverClass="hover:bg-gold/10 hover:text-gold"
        />
        <ActionButton
          onClick={() => onCommand(device.id, 'screenshot')}
          icon="📷"
          label="Screenshot"
          tooltip="Capture screenshot · لقطة شاشة"
          hoverClass="hover:bg-teal/10 hover:text-teal"
        />
        <ActionButton
          onClick={onMessage}
          icon="✉"
          label="Message"
          tooltip="Send message · إرسال رسالة"
          hoverClass="hover:bg-accent/10 hover:text-accent"
        />
        <ActionButton
          onClick={() => onReissueToken(device.id)}
          icon="🔑"
          label="Token"
          tooltip="Reissue auth token · re-mint device token (recovery)"
          hoverClass="hover:bg-gold/10 hover:text-gold"
        />
        <ActionButton
          onClick={() => onResetClaim(device.id)}
          icon="♻"
          label="Reset"
          tooltip="Reset device · unclaim for re-pairing (destructive)"
          hoverClass="hover:bg-danger/10 hover:text-danger"
        />
        {device.company_id && (
          <EmergencyControl
            companyId={device.company_id}
            companyName={device.company_name}
            active={!!device.emergency_active}
            onChanged={onEmergencyChanged}
          />
        )}
      </div>
    </div>
  );
}

function ActionButton({ onClick, icon, label, tooltip, hoverClass }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      aria-label={tooltip}
      className={`flex-1 px-2 py-1.5 text-xs rounded-lg bg-white/[0.04] text-text-muted border border-white/[0.06] transition-colors inline-flex items-center justify-center gap-1 ${hoverClass}`}
    >
      <span aria-hidden="true">{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
